import { FC } from 'react';
import styled from 'styled-components';
import useTheme from '../../utils/useTheme';
import { v1 as uid } from 'uuid';

interface IProps {
  animationColor?: string;
  animationDuration?: string;
  height?: string;
  placeholderColor?: string;
  preserveAspectRatio?: string;
  responsive?: boolean;
  viewBox?: string;
}

const ContentPlaceholderWrapper = styled.div<IProps>`
  height: ${({ responsive }) => (responsive ? '100%' : null)};
  position: ${({ responsive }) => (responsive ? 'relative' : null)};
  width: ${({ responsive }) => (responsive ? '100%' : null)};
`;

const ContentPlaceholderElement = styled.svg<IProps>`
  height: ${({ height }) => height};
  left: ${({ responsive }) => (responsive ? 0 : null)};
  position: ${({ responsive }) => (responsive ? 'absolute' : null)};
  top: ${({ responsive }) => (responsive ? 0 : null)};
  width: 100%;
`;

export const ContentPlaceholder: FC<IProps> = ({
  animationColor,
  animationDuration = '2s',
  children,
  height,
  placeholderColor,
  preserveAspectRatio,
  responsive,
  viewBox,
}) => {
  const theme = useTheme();

  const id = uid();
  const gradientId = `gradient-${id}`;
  const clipPathId = `clip-${id}`;

  const placeholderBackground = placeholderColor ?? theme.vars.newColor.lightGS2;
  const animationBackground = animationColor ?? theme.vars.newColor.lightGS1;

  return (
    <ContentPlaceholderWrapper data-testid="ContentPlaceholderWrapper" responsive={responsive}>
      <ContentPlaceholderElement
        data-testid="ContentPlaceholder"
        height={height || '100%'}
        viewBox={viewBox}
        preserveAspectRatio={preserveAspectRatio}
        responsive={responsive}
      >
        <rect
          style={{ fill: `url(#${gradientId})` }}
          clipPath={`url(#${clipPathId})`}
          x="0"
          y="0"
          width="100%"
          height="100%"
        />

        <defs>
          <clipPath id={clipPathId}>{children}</clipPath>

          <linearGradient id={gradientId}>
            <stop offset="0%" stopColor={placeholderBackground} stopOpacity={1}>
              <animate attributeName="offset" values="-2; 1" dur={animationDuration} repeatCount="indefinite" />
            </stop>
            <stop offset="50%" stopColor={animationBackground} stopOpacity={1}>
              <animate attributeName="offset" values="-1.5; 1.5" dur={animationDuration} repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor={placeholderBackground} stopOpacity={1}>
              <animate attributeName="offset" values="-1; 2" dur={animationDuration} repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
      </ContentPlaceholderElement>
    </ContentPlaceholderWrapper>
  );
};

export default ContentPlaceholder;
